import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCw } from 'lucide-react';
import type { SyncProvider } from '../../lib/sync/provider';
import { createSaveState, type SaveState, type SaveView } from '../../lib/sync/save-state';
import { hasUnsaved } from './useLeaveGuard';
import { Button, cn, IconButton, Tooltip } from '../../ui';
import { Icon } from '../../ui/icons';

const noSubscribe = () => () => undefined;

const DOT: Record<SaveView, string> = {
  saved: 'bg-success',
  saving: 'bg-fg-subtle',
  offline: 'bg-warning',
  error: 'bg-danger',
  readOnly: 'bg-fg-subtle',
};

/**
 * The title pill's save state (backlog §19 P16-3.4), from the room's real acks: Saved (how long ago in a tooltip),
 * Saving…, Offline · N changes pending, Could not save · Retry, Read only. Screen readers hear only going offline, an
 * error and the recovery, never each save. `compact` (phones): a coloured dot named with the full words, plus the
 * short word when offline or read only.
 */
export function SaveIndicator({ sync, readOnly, compact = false }: { sync: SyncProvider; readOnly?: boolean; compact?: boolean }) {
  const { t } = useTranslation();
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  // Created and destroyed by the same effect: StrictMode's mount, unmount, mount must not leave a destroyed state.
  const [state, setState] = useState<SaveState | null>(null);
  useEffect(() => {
    const s = createSaveState(sync, { readOnly: () => !!readOnlyRef.current });
    setState(s);
    return () => s.destroy();
  }, [sync]);
  const view = useSyncExternalStore(state?.subscribe ?? noSubscribe, () => state?.view() ?? 'saved');
  // Pending count changes do not change the view: read it too.
  const pending = useSyncExternalStore(sync.subscribe, () => sync.pendingCount);
  // What the leave guard asks about, exposed for tests and tools (`data-unsaved`).
  const unsaved = useSyncExternalStore(sync.subscribe, () => hasUnsaved(sync));
  const [, refresh] = useState(0);

  const [announce, setAnnounce] = useState('');
  const previous = useRef<SaveView>(view);
  useEffect(() => {
    const before = previous.current;
    previous.current = view;
    if (view === before) return;
    if (view === 'offline') setAnnounce(t('save.announce.offline'));
    else if (view === 'error') setAnnounce(t('save.announce.error'));
    else if (view === 'saved' && (before === 'offline' || before === 'error')) setAnnounce(t('save.announce.recovered'));
  }, [view, t]);

  const words =
    view === 'offline' ? (pending > 0 ? t('save.offlinePending', { count: pending }) : t('save.offline')) : view === 'error' ? t('save.error') : t(`save.${view}`);
  const since = state?.since() ?? null;
  const ago = view === 'saved' && since !== null ? t('save.savedAgo', { count: Math.max(0, Math.round((Date.now() - since) / 1000)) }) : words;

  return (
    <span data-save={view} data-unsaved={unsaved} className="inline-flex items-center gap-1.5" onPointerEnter={() => refresh((n) => n + 1)} onFocus={() => refresh((n) => n + 1)}>
      {compact ? (
        <>
          <span role="img" data-dot aria-label={words} title={words} className={cn('size-2.5 shrink-0 rounded-full', DOT[view])} />
          {/* Not by a colour alone (WCAG 1.4.1): the states that limit what can be done are also written (P12a). */}
          {(view === 'offline' || view === 'readOnly') && (
            <span aria-hidden="true" className="whitespace-nowrap font-mono text-xs text-fg-muted">
              {t(`save.${view}`)}
            </span>
          )}
        </>
      ) : (
        <Tooltip label={ago} side="bottom">
          {/* Focusable so the "how long ago" tooltip opens from the keyboard too. */}
          <span tabIndex={0} className={cn('rounded font-mono text-xs', view === 'error' ? 'text-danger' : 'text-fg-muted')}>
            {words}
          </span>
        </Tooltip>
      )}
      {view === 'error' &&
        (compact ? (
          <IconButton label={t('save.retry')} size="sm" icon={<Icon icon={RotateCw} size={14} />} onClick={() => state?.retry()} />
        ) : (
          <Button size="sm" onClick={() => state?.retry()}>
            {t('save.retry')}
          </Button>
        ))}
      <span aria-live="polite" className="sr-only">
        {announce}
      </span>
    </span>
  );
}
